import { GetTimeslotsResponse } from '@src/@data/types/reservations';
import { useMessages } from '@src/@data/utility/hooks/useMessages';
import classNames from 'classnames';
import React, { useCallback } from 'react';
import { Plus, X } from 'react-feather';
import { Button, FormGroup } from 'reactstrap';
import { AdditionalDateAndTimeRow } from './AdditionalDateAndTimeRow';
import {
  DateAndTimeRow,
  DateAndTimeRowData,
  DateAndTimeRowProps,
} from './DateAndTimeRow';

interface DateAndTimeFormGroupProps
  extends Omit<DateAndTimeRowProps, 'className'> {
  multipleRowsProps?: {
    additionalRows: Pick<DateAndTimeRowData, 'id' | 'startTime'>[];
    getTimeslotsFromStartDate: (
      startDate: Date
    ) => Promise<GetTimeslotsResponse | undefined>;
    addRow: () => void;
    removeRow: (id: number) => void;
    updateRow: (id: number, startTime?: Date) => void;
  };
  duration?: number;
}

const FIRST_ROW_ID = 'first-row';

export const DateAndTimeFormGroup: React.FC<DateAndTimeFormGroupProps> = ({
  row,
  multipleRowsProps,
  areTimeslotsDisabled,
  menuPlacement,
  isClientReservation = true,
  dateChangeDisabled,
  duration,
}) => {
  const { messages } = useMessages();

  const getSelectedTimeslots = useCallback(
    (rowId: string | number) => {
      if (!multipleRowsProps?.additionalRows) {
        return;
      }
      return [
        { ...row, id: FIRST_ROW_ID },
        ...multipleRowsProps.additionalRows,
      ].reduce((res, row) => {
        return row.startTime && row.id !== rowId
          ? [...res, row.startTime]
          : res;
      }, [] as Date[]);
    },
    [multipleRowsProps?.additionalRows, row]
  );

  return (
    <FormGroup>
      <DateAndTimeRow
        row={row}
        areTimeslotsDisabled={areTimeslotsDisabled}
        menuPlacement={menuPlacement}
        isClientReservation={isClientReservation}
        dateChangeDisabled={dateChangeDisabled}
        selectedTimeslots={getSelectedTimeslots(FIRST_ROW_ID)}
        duration={duration}
      />
      {multipleRowsProps?.additionalRows?.map(({ id }) => (
        <AdditionalDateAndTimeRow
          key={id}
          id={id}
          defaultStartDate={row.startDate}
          getTimeslotsFromStartDate={
            multipleRowsProps.getTimeslotsFromStartDate
          }
          selectedTimeslots={getSelectedTimeslots(id) ?? []}
          areTimeslotsDisabled={areTimeslotsDisabled}
          dateChangeDisabled={dateChangeDisabled}
          menuPlacement={menuPlacement}
          isClientReservation={isClientReservation}
          className="mt-1"
          onRowChange={multipleRowsProps.updateRow}
          duration={duration}
        />
      ))}
      {!!multipleRowsProps && (
        <div className="d-flex mt-1">
          <Button
            color="primary"
            outline
            size="sm"
            className={classNames(
              'd-flex align-items-center',
              !!multipleRowsProps.additionalRows?.length && 'mr-1 w-100'
            )}
            onClick={multipleRowsProps.addRow}
          >
            <Plus size={16} className="mr-50" />
            {messages('add_reservation_slot_add_date_time')}
          </Button>
          {!!multipleRowsProps.additionalRows?.length && (
            <Button
              color="danger"
              outline
              size="sm"
              onClick={() =>
                multipleRowsProps.removeRow(
                  multipleRowsProps.additionalRows[
                    multipleRowsProps.additionalRows.length - 1
                  ].id
                )
              }
              className="d-flex align-items-center w-100"
            >
              <X size={16} className="mr-50" />
              {messages('add_reservation_slot_remove_last_date_time')}
            </Button>
          )}
        </div>
      )}
    </FormGroup>
  );
};
